import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import { Alert, Grid, Stack, TextField } from '@mui/material'
import colors from '@/assets/scss/_themes-vars.module.scss'
import { useRequestHandler } from '@/hooks/useRequestHandler'
import AudioViewer from '@/components/ui/preview/AudioViewer'
import TabPanel from '@/components/ui/tab/TabPanel'
import { useIsSA } from '@/hooks/useIsSA'
import { useBillingDialogEnabled } from '@/hooks/useBillingDialogEnabled'
import { useGetWaitingRoomQuery } from '@/core/services/waiting-room'
import { ConsultationStatus } from '@/core/models/consultation.model'
import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { selectConsultationUpdateRequestTracker, updateConsultation } from '@/redux/reducers/consultation.reducer'
import { useGetConsultationQuery } from '@/core/services/consultation'
import { skipToken } from '@reduxjs/toolkit/query'
import QueryBoundary from '@/components/ui/template/QueryBoundary'
import { createExpertise, selectExpertiseCreateRequestTracker, selectNewlyCreatedExpertiseId } from '@/redux/reducers/expertise.reducer'
import { isStaff } from '@/utils/type-guards'
import BillingReviewCard from './components/BillingReviewCard'
import ConsultationHeader, { ConsultationHeaderProps } from './components/ConsultationHeader'
import Messenger from './containers/Messenger'
import PatientProfile from './components/PatientProfile'
import VitalData from './containers/VitalData'
import Documents from '../diagnostic/containers/Documents'
import ConsultationFormsContainer from './containers/ConsultationFormsContainer'
import { TranscriptionViewer } from './components/TranscriptionViewer'
import { ReportViewer } from './components/ReportViewer'
import { Allotment } from 'allotment'
import _ from 'lodash'
import { TranscriptionAudioPlayer } from './components/TranscriptionAudioPlayer'
import { useAbility } from '@/providers/AbilityProvider'
import { useLingui } from '@lingui/react'
import { msg } from '@lingui/macro'
import { useMedicalActsLicenseEnabled } from '@/features/medical-acts/model/useMedicalActsLicenseEnabled'
import { MedicalActsSection } from '@/features/medical-acts/ui/MedicalActsSection'

type ConsultationPageProps = {}

const defaultSize = [60, 40]

export default function ConsultationPage(props: ConsultationPageProps) {
  const lingui = useLingui()
  const ability = useAbility()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { consultationId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const canViewForms = ability.can('list', 'FormSubmission') && ability.can('list', 'FormTemplate')
  const hasMedicalActsLicense = useMedicalActsLicenseEnabled()
  const isSA = useIsSA()

  const tabs = useMemo(
    () =>
      _.compact([
        { name: 'patientProfile', label: t('page.consultation.tab.patientProfile') },
        { name: 'vitalData', label: t('page.consultation.tab.vitalData') },
        // SEHA does not bill through Medical Acts, so the tab is hidden there.
        hasMedicalActsLicense && !isSA && { name: 'medicalActs', label: t('page.consultation.tab.medicalActs', { defaultValue: 'Medical Acts' }) },
        { name: 'documents', label: t('page.consultation.tab.documents') },
        canViewForms && { name: 'forms', label: lingui._(msg`Forms`) },
        { name: 'transcriptions', label: t('page.consultation.tab.transcriptions') },
        { name: 'conversation', label: t('page.consultation.tab.conversation') },
      ]),
    [canViewForms, lingui, t, hasMedicalActsLicense, isSA]
  )
  const [currentTab, setCurrentTab] = useState<string>(tabs[0].name)

  const authUser = useAppSelector(selectAuthUser)
  const { data: consultation, isLoading, isError, error } = useGetConsultationQuery(consultationId ?? skipToken)

  // Desmos billing is opt-in per waiting room and per staff role. The live
  // consultation already honours that config; read the same switch here so an
  // old consultation does not expose billing to users the room excludes.
  const { data: waitingRoom } = useGetWaitingRoomQuery(consultation?.waitingRoom ?? skipToken)
  const billingDialogEnabledForUser = useBillingDialogEnabled(waitingRoom)
  const newlyCreatedExpertiseId = useAppSelector(selectNewlyCreatedExpertiseId)
  const dictaphones = useMemo(() => {
    return (
      consultation?.dictaphones.map((dictaphone) => ({
        filename: dictaphone.filename,
        key: dictaphone.key,
        src: dictaphone.url,
        text: dictaphone.text ?? '',
      })) ?? []
    )
  }, [consultation])

  const updateConsultationRequestHandler = useRequestHandler({
    onSuccess: () => enqueueSnackbar(t('notification.consultation.update.success'), { variant: 'success' }),
    onError: () => enqueueSnackbar(t('notification.consultation.update.error'), { variant: 'error' }),
    requestTrackerSelector: selectConsultationUpdateRequestTracker,
  })

  const { dispatch: createExpertiseDispatch } = useRequestHandler({
    onSuccess: () => {
      enqueueSnackbar(t('notification.expertise.create.success'), { variant: 'success' })

      navigate(`/expertises/${newlyCreatedExpertiseId}`)
    },
    onError: () => enqueueSnackbar(t('notification.consultation.end.error'), { variant: 'error' }),
    requestTrackerSelector: selectExpertiseCreateRequestTracker,
  })

  const handleSelectTab = (tab: { name: string; label: string }) => () => {
    setCurrentTab(tab.name)
  }

  useEffect(() => {
    const action = searchParams.get('action')

    if (!action || !consultation) return

    let status: ConsultationStatus | undefined
    if (consultation.status === ConsultationStatus.Scheduled) {
      switch (action) {
        case 'accept':
          status = ConsultationStatus.Accepted
          break
        case 'refuse':
          status = ConsultationStatus.Refused
          break
        default:
          break
      }
    }

    searchParams.delete('action')
    setSearchParams(searchParams.toString())

    if (!!status) dispatch(updateConsultation(consultation._id, { status }))
  }, [consultation, dispatch, searchParams, setSearchParams])

  const handleGenerateExpertise = useCallback(() => {
    if (consultationId) {
      createExpertiseDispatch(
        createExpertise({
          expertise: {
            patient: consultation?.patient._id,
            consultations: [consultationId],
          },
        })
      )
    }
  }, [consultation, consultationId, createExpertiseDispatch])

  const handleCancelConsultation = useCallback(() => {
    if (consultationId) updateConsultationRequestHandler.dispatch(updateConsultation(consultationId, { status: ConsultationStatus.Canceled }))
  }, [consultationId, updateConsultationRequestHandler])

  const menuActions: ConsultationHeaderProps['actions'] = useMemo(() => {
    if (!consultation) return []
    const actions = [
      {
        label: t('page.consultation.actions.generateExpertise'),
        actionHandler: handleGenerateExpertise,
        fontColor: 'black',
        status: [ConsultationStatus.Completed],
      },
      {
        label: t('page.consultation.actions.join'),
        actionHandler: () => navigate(`live`),
        fontColor: 'black',
        status: [ConsultationStatus.Accepted, ConsultationStatus.Active, ConsultationStatus.Scheduled],
      },
      {
        label: t('page.consultation.actions.cancel'),
        actionHandler: handleCancelConsultation,
        fontColor: 'red',
        status: [ConsultationStatus.Accepted, ConsultationStatus.Scheduled],
      },
    ]
    return actions.filter((action) => action.status.includes(consultation.status)).map((action) => _.omit(action, ['status']))
  }, [consultation, navigate, t, handleCancelConsultation, handleGenerateExpertise])

  if (!consultation) {
    return <QueryBoundary isLoading={isLoading} isError={isError} error={error} />
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ pt: '20px', pb: '20px' }}>
        <Grid container>
          <Grid
            item
            xs={12}
            sm={12}
            md={12}
            lg={12}
            xl={12}
            sx={{
              marginBottom: '20px',
            }}
          >
            <ConsultationHeader actions={menuActions} isDoctor={isStaff(authUser) && authUser?.status === 'MEDECIN'} consultation={consultation} />
          </Grid>
          <Grid container spacing={3}>
            {tabs.map((tab) => (
              <Grid item xs={12} sm={6} md={4} lg={12 / tabs.length} key={tab.name}>
                <Button
                  fullWidth
                  onClick={handleSelectTab(tab)}
                  sx={[
                    {
                      color: colors.gray500,
                      fontSize: '14px',
                      fontWeight: 500,
                    },
                    tab.name === currentTab && {
                      backgroundColor: colors.indigo100,
                      color: colors.indigo700,
                      '&:hover': {
                        backgroundColor: colors.indigo100,
                      },
                    },
                  ]}
                >
                  {tab.label}
                </Button>
              </Grid>
            ))}
          </Grid>
        </Grid>
        <Box sx={{ mt: '28px' }}>
          {consultation?.status === ConsultationStatus.Completed && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('page.consultation.closed.message')}
            </Alert>
          )}
          {billingDialogEnabledForUser && <BillingReviewCard consultation={consultation} />}
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TabPanel value="patientProfile" index={currentTab}>
                <PatientProfile consultation={consultation} />
              </TabPanel>
              <TabPanel value="vitalData" index={currentTab}>
                <VitalData consultationId={consultation._id} />
              </TabPanel>
              {hasMedicalActsLicense && (
                <TabPanel value="medicalActs" index={currentTab}>
                  <MedicalActsSection patientId={consultation.patient._id} consultationId={consultation._id} readOnly={false} />
                </TabPanel>
              )}
              <TabPanel value="documents" index={currentTab}>
                <Documents consultationId={consultation._id} />
              </TabPanel>
              <TabPanel value="transcriptions" index={currentTab}>
                <Stack spacing={3}>
                  {dictaphones?.map((dictaphone) => (
                    <Stack key={dictaphone.key} spacing={2}>
                      <AudioViewer src={dictaphone.src} sx={{ width: '100%' }} />
                      <TextField fullWidth InputProps={{ readOnly: true }} minRows={4} multiline={true} defaultValue={dictaphone.text} />
                    </Stack>
                  ))}
                  {consultation?.transcription && (
                    <Box sx={{ height: '530px', overflow: 'hidden' }}>
                      <Allotment defaultSizes={defaultSize}>
                        <Allotment.Pane minSize={1}>
                          <Box sx={{ height: '100%', overflowY: 'auto' }}>
                            <ReportViewer consultationId={consultation._id} userId={authUser?._id!} />
                          </Box>
                        </Allotment.Pane>
                        <Allotment.Pane minSize={1}>
                          <Box sx={{ height: '100%', overflowY: 'auto' }}>
                            <TranscriptionViewer transcriptionId={consultation?.transcription} />
                          </Box>
                        </Allotment.Pane>
                      </Allotment>
                    </Box>
                  )}
                  {consultation?.transcription && consultation.type !== 'local' && <TranscriptionAudioPlayer transcriptionId={consultation.transcription} />}
                </Stack>
              </TabPanel>
              <TabPanel value="conversation" index={currentTab}>
                <Messenger consultationStatus={consultation.status} chatRoomId={consultation.chatRoom} />
              </TabPanel>
              {canViewForms && (
                <TabPanel value="forms" index={currentTab}>
                  <ConsultationFormsContainer consultationId={consultation._id} />
                </TabPanel>
              )}
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Container>
  )
}
